// Pure helpers for the Trades grid: search, outcome filter, sort, and the
// flat list the chart viewer steps through. Kept free of React/Firebase so
// they can be unit tested directly.
import { classifyTradeOutcomeByThreshold, DEFAULT_BE_THRESHOLD } from '../statsUtils';
import { isTapeReading } from './tradeHelpers';

export const TRADE_OUTCOME_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'win', label: 'Win' },
  { value: 'loss', label: 'Loss' },
  { value: 'breakeven', label: 'BE', ariaLabel: 'Break-even' },
];

export const TRADE_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'biggestWin', label: 'Biggest win' },
  { value: 'biggestLoss', label: 'Biggest loss' },
];

const hasPnl = (entry) => {
  const p = entry?.pnl;
  if (p === undefined || p === null || p === '') return false;
  return Number.isFinite(Number(p));
};

/** True for real trades with a P&L (not deposits, payouts or tape readings). */
export const isOutcomeTrade = (entry) =>
  !!entry && !entry.isDeposit && !entry.isPayout && !isTapeReading(entry) && hasPnl(entry);

/** 'win' | 'loss' | 'breakeven' using the same threshold rule as Summary/Calendar, or null. */
export function getTradeOutcome(entry, beThreshold = DEFAULT_BE_THRESHOLD) {
  if (!isOutcomeTrade(entry)) return null;
  return classifyTradeOutcomeByThreshold(entry.pnl, beThreshold);
}

const SEARCH_FIELDS = ['title', 'tickerTraded', 'ticker', 'notes', 'accountName', 'pdArray'];

const SESSION_DISPLAY_NAMES = {
  'pre-market': 'Pre-market',
  'NY-AM': 'NY AM',
  'NY-Lch': 'NY Lunch',
  'NY-PM': 'NY PM',
};
const MAX_MODEL_LENGTH = 40;

/** "NY AM · FVG" from tradeSession + pdArray (free text), or '' when neither is set. */
export function formatSessionModel(entry) {
  if (!entry || entry.isDeposit || entry.isPayout) return '';
  const rawSession = typeof entry.tradeSession === 'string' ? entry.tradeSession.trim() : '';
  const session = SESSION_DISPLAY_NAMES[rawSession] || rawSession;
  let model = typeof entry.pdArray === 'string' ? entry.pdArray.replace(/\s+/g, ' ').trim() : '';
  if (model.length > MAX_MODEL_LENGTH) model = `${model.slice(0, MAX_MODEL_LENGTH - 1).trimEnd()}…`;
  return [session, model].filter(Boolean).join(' · ');
}

/** Every whitespace-separated term must appear in one of the searchable fields. */
export function matchesTradeSearch(entry, query) {
  const terms = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = SEARCH_FIELDS
    .map((field) => entry?.[field])
    .filter((value) => typeof value === 'string' || typeof value === 'number')
    .join(' ')
    .toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/**
 * Applies search + outcome and sorts. Input groups are expected newest-first
 * (the order the page builds them in), so newest/oldest keep that order.
 */
export function filterAndSortTradeGroups(groups, { query = '', outcome = 'all', sort = 'newest', beThreshold = DEFAULT_BE_THRESHOLD } = {}) {
  const indexed = (groups || [])
    .map((group, index) => ({ group, index }))
    .filter(({ group }) => {
      const entry = group?.entry;
      if (!entry) return false;
      if (outcome !== 'all' && getTradeOutcome(entry, beThreshold) !== outcome) return false;
      return matchesTradeSearch(entry, query);
    });

  if (sort === 'oldest') {
    indexed.sort((a, b) => b.index - a.index);
  } else if (sort === 'biggestWin' || sort === 'biggestLoss') {
    const direction = sort === 'biggestWin' ? -1 : 1;
    indexed.sort((a, b) => {
      const aHas = isOutcomeTrade(a.group.entry);
      const bHas = isOutcomeTrade(b.group.entry);
      // Entries without a trade P&L always go last.
      if (aHas !== bHas) return aHas ? -1 : 1;
      if (aHas) {
        const diff = (Number(a.group.entry.pnl) - Number(b.group.entry.pnl)) * direction;
        if (diff !== 0) return diff;
      }
      return a.index - b.index;
    });
  }
  return indexed.map(({ group }) => group);
}

/** Stable id for one viewer stop: a single chart of an entry, or the entry itself when it has none. */
export function viewerItemKey(entry, imageIndex = null) {
  const owner = `${entry?.isOrphaned ? 'orphan' : 'acct'}:${entry?.accountId ?? ''}:${entry?.id ?? entry?.created ?? ''}`;
  return `${owner}#${imageIndex === null || imageIndex === undefined ? 'entry' : imageIndex}`;
}

/** Flattens the visible groups into the order ← / → steps through. */
export function buildViewerItems(groups) {
  const items = [];
  (groups || []).forEach((group) => {
    if (!group?.entry) return;
    if (group.images && group.images.length) {
      group.images.forEach((image, i) => {
        const imageIndex = image.imageIndex ?? i;
        items.push({ key: viewerItemKey(group.entry, imageIndex), src: image.src, imageIndex, entry: group.entry });
      });
    } else {
      items.push({ key: viewerItemKey(group.entry, null), src: null, imageIndex: null, entry: group.entry });
    }
  });
  return items;
}

/** Wrap-around step; returns -1 when there is nothing to step to. */
export function stepIndex(length, current, direction) {
  if (!length) return -1;
  const safe = current >= 0 && current < length ? current : 0;
  return direction === 'prev' ? (safe - 1 + length) % length : (safe + 1) % length;
}

const cleanTimestamp = (created) => {
  if (typeof created !== 'string') return '';
  const lastDash = created.lastIndexOf('-');
  return lastDash > 10 ? created.substring(0, lastDash) : created;
};

/** Display date for a card: year/month/day fields first, then the created timestamp. */
export function formatEntryDate(entry) {
  if (entry && entry.month && entry.day) {
    const year = entry.year ? parseInt(entry.year, 10) : new Date().getFullYear();
    const month = parseInt(entry.month, 10) - 1;
    const day = parseInt(entry.day, 10);
    if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)
      && year >= 2000 && year <= 2100 && month >= 0 && month <= 11 && day >= 1 && day <= 31) {
      const date = new Date(year, month, day);
      if (!Number.isNaN(date.getTime())) return date.toLocaleDateString();
    }
  }
  if (typeof entry?.created === 'string') {
    const date = new Date(cleanTimestamp(entry.created));
    if (!Number.isNaN(date.getTime())) return date.toLocaleDateString();
    const match = entry.created.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, y, m, d] = match;
      const fallback = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      if (!Number.isNaN(fallback.getTime())) return fallback.toLocaleDateString();
    }
  }
  return 'No date';
}
